import {createContext, FC, useContext, useEffect, useState} from 'react'
import {
  TAllSmsStatus,
  TPushServerConfig,
  TSmsFilterColumnName,
  TSmsSummaryType,
} from '../../modules/analyze/components/reports/_model'
import {ISelectOption} from '../core/_model'
import {BASE_URL} from '../../modules/config'
import {WithChildren} from '../../../_metronic/helpers'
import {useAuth} from '../../modules/auth'
import axios from 'axios'
import {INotification} from '../../modules/notifications/_model'
import {useQuery} from 'react-query'

type RittAppContextType = {
  timeRange: ISelectOption[]
  recurringTimeRange: ISelectOption[]
  smsSummaryType: TSmsSummaryType[]
  allSmsStatus: TAllSmsStatus[]
  smsFilterColumnName: TSmsFilterColumnName[]
  userInfo: ISelectOption[]
  pushServerConfig: ISelectOption[]
  configServers: TPushServerConfig[]
  emailOptions: ISelectOption[]
  getEntities: () => void
  accountOptions: ISelectOption[]
  networkNameOptions: ISelectOption[]
  senderIdsOptions: ISelectOption[]
  notification: INotification[] | undefined
}

const networkNames = [
  {id: 0, value: 'GP', label: 'GP'},
  {id: 0, value: 'Banglalink', label: 'Banglalink'},
  {id: 0, value: 'Tele-Talk', label: 'Tele-Talk'},
  {id: 0, value: 'Robi', label: 'Robi'},
]

const RittAppContext = createContext<RittAppContextType | null>(null)

const GET_ENTITIES_URL = `${BASE_URL}/api/protect/allExportEntity`
const GET_NOTIFICATION_LIST_URL = `${BASE_URL}/api/protect/getNotificationList`

const getNotificationList = async (token: string): Promise<INotification[]> => {
  const response = await axios.get(GET_NOTIFICATION_LIST_URL, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  return response.data
}

const formatOptionsSelections = (
  options: {id: number; [key: string]: string | number}[],
  keyname: string
): ISelectOption[] => {
  if (!options || options.length <= 0) return []

  return options.map((opt, idx) => ({
    id: opt.id ?? idx,
    value: String(opt[keyname]),
    label: String(opt[keyname]),
  }))
}

// const formatSenderOptions = (options: {sender: string}[]): ISelectOption[] => {
//   console.log(options)
//   const formatted: ISelectOption[] = options.map((opt, idx) => ({
//     id: idx + 1,
//     value: opt,
//     label: opt,
//   }))

//   return formatted || []
// }

const RittAppProvider: FC<WithChildren> = ({children}) => {
  const {auth, currentUser} = useAuth()
  const [timeRange, setTimeRange] = useState<ISelectOption[]>([])
  const [recurringTimeRange, setRecurringTimeRange] = useState<ISelectOption[]>([])
  const [smsSummaryType, setSmsSummaryType] = useState<TSmsSummaryType[]>([])
  const [allSmsStatus, setAllSmsStatus] = useState<TAllSmsStatus[]>([])
  const [smsFilterColumnName, setSmsFilterColumnName] = useState<TSmsFilterColumnName[]>([])
  const [userInfo, setUserInfo] = useState<ISelectOption[]>([])
  const [pushServerConfig, setPushServerConfig] = useState<ISelectOption[]>([])
  const [emailOptions, setEmailOptions] = useState<ISelectOption[]>([])
  const [accountOptions, setAccountOptions] = useState<ISelectOption[]>([])
  const [networkNameOptions, setNetworkNameOptions] = useState<ISelectOption[]>(networkNames)
  const [senderIdsOptions, setSenderIdsOptions] = useState<ISelectOption[]>([])
  const [configServers, setConfigServers] = useState<TPushServerConfig[]>([])

  const getEntities = async () => {
    try {
      const response = await axios.get(GET_ENTITIES_URL, {
        headers: {
          Authorization: `Bearer ${auth?.accessToken}`,
        },
      })
      const result = await response.data
      // console.log(result.senderLIstEntities)
      formattingRecurringTimeRange(result.iatSearchTimeRange, 'time_range_name')
      formattingOneTimeRange(result.iatSearchTimeRange, 'time_range_name')
      setSmsSummaryType(result.iatSmsSummeryType || [])
      setAllSmsStatus(result.iatAllSmsStatus || [])
      setSmsFilterColumnName(result.iatSmsFilterColumnNames || [])
      setUserInfo(formatOptionsSelections(result.userInfo, 'user_full_name'))
      setPushServerConfig(formatOptionsSelections(result.iatlPushServerConfigs, 'config_name'))
      setSenderIdsOptions(formatOptionsSelections(result.senderLIstEntities, 'sender'))
      setConfigServers(formattingServers(result.iatlPushServerConfigs))
      // console.log(result.iatlPushServerConfigs)
    } catch (error) {
      console.log(error)
    }
  }

  const {
    isLoading,
    data,
    error,
    refetch: notificationFetch,
  } = useQuery({
    queryKey: ['notificationLit'],
    queryFn: () => getNotificationList(auth?.accessToken!),
    enabled: !!auth?.accessToken && !!currentUser,
    staleTime: Infinity, // Prevents automatic refetch
    cacheTime: Infinity, // Keeps data in cache indefinitely
    refetchOnMount: false, // Prevents refetch when component mounts
  })

  const formattingServers = (options: {id: number; [key: string]: string | number}[]) => {
    if (!options || options.length <= 0) return []

    const servers = options.map((opt) => ({
      id: opt.id,
      value: String(opt.destination_type),
      label: String(opt.destination_type),
      destination_type: String(opt.destination_type),
      status: String(opt.status),
      host_or_ip: String(opt.host_or_ip),
      port: typeof opt.port === 'string' ? parseInt(opt.port) : opt.port,
      config_name: String(opt.config_name),
    }))

    return servers
  }

  const formattingRecurringTimeRange = (
    options: {id: number; [key: string]: string | number}[],
    keyname: string
  ) => {
    const rangesIds = [2, 3, 10, 11]

    const filtered = options.filter((opt) => {
      return rangesIds.some((rangeId) => Object.values(opt).includes(rangeId))
    })

    const filteredRange = options.filter((opt) => opt.id !== 12)

    setRecurringTimeRange(formatOptionsSelections(filteredRange, keyname))
  }

  const formattingOneTimeRange = (
    options: {id: number; [key: string]: string | number}[],
    keyname: string
  ) => {
    // const rangesIds = [10, 11]

    const filtered = options.filter((opt) => {
      return opt.id !== 10 && opt.id !== 11
    })

    setTimeRange(formatOptionsSelections(filtered, 'time_range_name'))
  }

  useEffect(() => {
    if (currentUser) {
      getEntities()
      notificationFetch()
      const userEmail = currentUser.userEmail
      const accounts = currentUser.user_full_name

      const emails = [
        {
          id: 1,
          value: userEmail!,
          label: userEmail!,
        },
      ]

      const accountsAsOptions = [
        {
          id: currentUser.id!,
          value: accounts,
          label: accounts,
        },
      ]

      setEmailOptions(emails)
      setAccountOptions(accountsAsOptions)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser])

  return (
    <RittAppContext.Provider
      value={{
        timeRange,
        recurringTimeRange,
        smsSummaryType,
        allSmsStatus,
        smsFilterColumnName,
        userInfo,
        pushServerConfig,
        emailOptions,
        getEntities,
        accountOptions,
        networkNameOptions,
        senderIdsOptions,
        configServers,
        notification: data,
      }}
    >
      {children}
    </RittAppContext.Provider>
  )
}

const useRittApp = () => {
  const context = useContext(RittAppContext)

  if (!context) {
    throw new Error('Something went wrong')
  }

  return context
}

export {useRittApp, RittAppProvider}
